/**
 * Wing commands over M1's AI states (M6 spec §6.2; plan P25): the command API the radial menu (P31), the HUD and the
 * fleet alerts call, the per-tick wing update and the commanded-state overlay `stepAIPilots` reads (`src/ai/pilot.ts`).
 *
 * | command      | target                                   | the overlay flies                         | M1 states run          |
 * |--------------|------------------------------------------|-------------------------------------------|------------------------|
 * | formUp       | M1's nearest (not engaged)               | the formation slot (wedge or line)        | never                  |
 * | attack       | the player's target (`Target.eid`)       | closing at boost while beyond engage range | in range (Engage)      |
 * | defend       | the player's attacker, or a hostile pilot targeting the player, within `defendRadiusM` | as attack | in range |
 * | hold         | M1's nearest (not engaged)               | the point where the command was given     | never                  |
 * | breakEngage  | M1's nearest                             | the formation slot while none in range    | in range (free M1)     |
 * | dock         | M1's nearest (not engaged)               | home (the escort's post); then released   | never                  |
 *
 * Dock/return ends `dockArriveM` off the home station's hull (`d − homeR`, scale SC-5e2; a point home has R 0).
 *
 * With no attack or defend target the wing forms up. A fleeing wingman (M1's Flee rule) runs on attack, defend and
 * break; on the flying commands it keeps flying them. Allocation-free per tick (D-014); float64 positions (D-013).
 */
import { createAiEventReader, isLiveShip } from '../ai/perception'
import { AI_STATE, enterEngage, type AiShips, type PilotOverlay } from '../ai/pilot'
import { steerToward } from '../ai/steering'
import { firstReadable, eventSlot, EV } from '../combat/events'
import { isHostile } from '../combat/hostility'
import { clamp, inverseRotateVec, rotateVec, type Quat, type Vec3 } from '../core/math'
import type { AiData, ShipDef } from '../data/schemas'
import type { Sim } from '../sim'
import { AIPilot, Control, Life, Pos64, Rot, Target, Team, Vel } from '../world/components'
import { FORMATION, writeFormation, type FormationName } from './formation'
import { dropMember, WING_CMD, WING_CMD_NAMES, type Wing, type WingCommandName } from './wing'

/**
 * Issues a wing command (the radial menu, P31). `formation` applies to formUp (default: the current one). Hold
 * position records each wingman's current point. Every command but Dock/return recalls ships that went home.
 */
export function issueWingCommand(wing: Wing, cmd: WingCommandName, formation?: FormationName): void {
  wing.command = WING_CMD[cmd]
  if (formation !== undefined) wing.formation = FORMATION[formation]
  if (cmd === 'hold') {
    for (let i = 0; i < wing.count; i++) {
      const e = wing.eid[i]
      wing.hold[i * 3] = Pos64.x[e]
      wing.hold[i * 3 + 1] = Pos64.y[e]
      wing.hold[i * 3 + 2] = Pos64.z[e]
    }
  }
  if (cmd === 'dock') wing.docked.fill(0)
  else wing.recall = true
}

/** The current command's name. */
export function wingCommandName(wing: Wing): WingCommandName {
  return WING_CMD_NAMES[wing.command]
}

/** True when `t` is a ship the wing may be sent at: alive, in `all`, not the leader, a wingman or on the leader's team. */
function attackable(wing: Wing, all: ArrayLike<number>, t: number): boolean {
  if (t < 0 || t === wing.leader || (t < wing.index.length && wing.index[t] > 0)) return false
  if (wing.leader >= 0 && Team.id[t] === Team.id[wing.leader]) return false
  return isLiveShip(all, t)
}

function leaderDistSq(wing: Wing, t: number): number {
  const p = wing.leader
  const dx = Pos64.x[t] - Pos64.x[p]
  const dy = Pos64.y[t] - Pos64.y[p]
  const dz = Pos64.z[t] - Pos64.z[p]
  return dx * dx + dy * dy + dz * dz
}

/** The defend target: the player's recent attacker, else the nearest hostile pilot targeting the player, within the radius. */
function defendTarget(wing: Wing, sim: Sim): number {
  const t = wing.cfg.tuning
  const r2 = t.defendRadiusM * t.defendRadiusM
  const all = sim.ships.dense
  const a = wing.attacker
  if (a >= 0 && sim.time - wing.attackerAt <= t.attackerMemoryS && attackable(wing, all, a) && leaderDistSq(wing, a) <= r2) return a
  const team = Team.id[wing.leader]
  const pilots = sim.pilots.dense
  let best = -1
  let bestD2 = r2
  for (let i = 0; i < pilots.length; i++) {
    const e = pilots[i]
    if (AIPilot.target[e] !== wing.leader || Life.alive[e] !== 1 || !isHostile(Team.id[e], team) || !attackable(wing, all, e)) continue
    const d2 = leaderDistSq(wing, e)
    if (d2 <= bestD2) {
      bestD2 = d2
      best = e
    }
  }
  return best
}

/**
 * The per-tick wing update (before `stepAIPilots`): drops dead members, reads the player's attackers from the combat
 * event ring, refreshes the formation slots and the attack or defend focus. Allocation-free (the event reader is
 * created once per sim).
 */
export function updateWing(wing: Wing, sim: Sim): void {
  for (let i = wing.count - 1; i >= 0; i--) {
    // Dead: the kill reader reports it; the roster only forgets it.
    if (Life.alive[wing.eid[i]] !== 1) dropMember(wing, i)
  }
  if (wing.reader === null || wing.reader.ring !== sim.events) wing.reader = createAiEventReader(sim.events)
  const reader = wing.reader
  const ring = reader.ring
  const leader = wing.leader
  for (let i = firstReadable(ring, reader.cursor); i < ring.head; i++) {
    const s = eventSlot(i)
    const type = ring.type[s]
    if ((type === EV.hitShield || type === EV.hitHull) && leader >= 0 && ring.eid[s] === leader && ring.other[s] >= 0) {
      wing.attacker = ring.other[s]
      wing.attackerAt = sim.time
    }
  }
  reader.cursor = ring.head
  wing.focus = -1
  if (leader < 0 || Life.alive[leader] !== 1) return
  writeFormation(wing.formation, wing.count, wing.cfg.tuning.formation, leader, wing.slots)
  if (wing.command === WING_CMD.attack) {
    const t = Target.eid[leader]
    wing.focus = attackable(wing, sim.ships.dense, t) ? t : -1
  } else if (wing.command === WING_CMD.defend) {
    wing.focus = defendTarget(wing, sim)
  }
}

// Module scratch (D-014).
const q: Quat = { x: 0, y: 0, z: 0, w: 1 }
const local: Vec3 = { x: 0, y: 0, z: 0 }
const lfwd: Vec3 = { x: 0, y: 0, z: 0 }

/**
 * Station-keeping (flight assist: the stick is the desired local velocity over `maxSpeed`, as the traffic pilot
 * flies): want the point's velocity (vx, vy, vz) plus `slotGain` × the error, capped at `maxSpeed`; boost beyond
 * `boostM`. The nose turns to the point while farther than `alignM`, else to the leader's heading (`face` ≥ 0) or
 * stays. Writes `Control`; returns the distance to the point.
 */
function keepStation(wing: Wing, eid: number, def: ShipDef, px: number, py: number, pz: number, vx: number, vy: number, vz: number, face: number, steerGain: number, rollGain: number): number {
  const t = wing.cfg.tuning
  const ex = px - Pos64.x[eid]
  const ey = py - Pos64.y[eid]
  const ez = pz - Pos64.z[eid]
  const dist = Math.sqrt(ex * ex + ey * ey + ez * ez)
  let wx = vx + t.slotGain * ex
  let wy = vy + t.slotGain * ey
  let wz = vz + t.slotGain * ez
  const m = def.maxSpeed
  const sp = Math.sqrt(wx * wx + wy * wy + wz * wz)
  if (sp > m) {
    const k = m / sp
    wx *= k
    wy *= k
    wz *= k
  }
  q.x = Rot.x[eid]
  q.y = Rot.y[eid]
  q.z = Rot.z[eid]
  q.w = Rot.w[eid]
  inverseRotateVec(q, wx, wy, wz, local)
  Control.surge[eid] = clamp(-local.z / m, -1, 1)
  Control.sway[eid] = clamp(local.x / m, -1, 1)
  Control.heave[eid] = clamp(local.y / m, -1, 1)
  Control.boost[eid] = dist > t.boostM ? 1 : 0
  Control.firePrimary[eid] = Control.fireSecondary[eid] = 0
  if (dist > t.alignM) {
    steerToward(eid, ex, ey, ez, steerGain, rollGain)
  } else if (face >= 0) {
    q.x = Rot.x[face]
    q.y = Rot.y[face]
    q.z = Rot.z[face]
    q.w = Rot.w[face]
    rotateVec(q, 0, 0, -1, lfwd)
    steerToward(eid, lfwd.x, lfwd.y, lfwd.z, steerGain, rollGain)
  } else {
    Control.pitch[eid] = Control.yaw[eid] = Control.roll[eid] = 0
  }
  return dist
}

/** The commanded-state overlay for `wing` (install with `setPilotOverlay`). `ai` is the live M1 AI data. */
export function createWingOverlay(wing: Wing, ai: AiData): PilotOverlay {
  const flySlot = (eid: number, i: number, def: ShipDef): boolean => {
    const k = i * 3
    const p = wing.leader
    if (p < 0 || Life.alive[p] !== 1) {
      // No leader: hold where the wing was told last.
      keepStation(wing, eid, def, wing.hold[k], wing.hold[k + 1], wing.hold[k + 2], 0, 0, 0, -1, ai.tuning.steering.turnGain, ai.tuning.steering.rollGain)
      return true
    }
    keepStation(wing, eid, def, wing.slots[k], wing.slots[k + 1], wing.slots[k + 2], Vel.x[p], Vel.y[p], Vel.z[p], p, ai.tuning.steering.turnGain, ai.tuning.steering.rollGain)
    return true
  }
  /** Attack or defend `t`: close at boost while beyond the engage fraction, else hand the tick to M1's Engage. */
  const engage = (eid: number, i: number, def: ShipDef, t: number): boolean => {
    if (t < 0) return flySlot(eid, i, def)
    if (AIPilot.state[eid] === AI_STATE.flee) return false
    const dx = Pos64.x[t] - Pos64.x[eid]
    const dy = Pos64.y[t] - Pos64.y[eid]
    const dz = Pos64.z[t] - Pos64.z[eid]
    const r = ai.tuning.engageRange * wing.cfg.tuning.engageFraction
    if (dx * dx + dy * dy + dz * dz > r * r) {
      keepStation(wing, eid, def, Pos64.x[t], Pos64.y[t], Pos64.z[t], Vel.x[t], Vel.y[t], Vel.z[t], -1, ai.tuning.steering.turnGain, ai.tuning.steering.rollGain)
      Control.boost[eid] = 1
      return true
    }
    if (AIPilot.state[eid] === AI_STATE.patrol) enterEngage(eid)
    return false
  }
  return {
    commands: (eid) => eid >= 0 && eid < wing.index.length && wing.index[eid] > 0,
    target: (_eid: number, _ships: AiShips, nearest: number): number => {
      const c = wing.command
      if (c === WING_CMD.attack || c === WING_CMD.defend) return wing.focus
      return nearest
    },
    fly: (eid, def): boolean => {
      const i = wing.index[eid] - 1
      switch (wing.command) {
        case WING_CMD.attack:
        case WING_CMD.defend:
          return engage(eid, i, def, wing.focus)
        case WING_CMD.breakEngage: {
          if (AIPilot.state[eid] === AI_STATE.flee) return false
          const t = AIPilot.target[eid]
          if (t >= 0) {
            const dx = Pos64.x[t] - Pos64.x[eid]
            const dy = Pos64.y[t] - Pos64.y[eid]
            const dz = Pos64.z[t] - Pos64.z[eid]
            const r = ai.tuning.engageRange
            if (dx * dx + dy * dy + dz * dz <= r * r) return false
          }
          return flySlot(eid, i, def)
        }
        case WING_CMD.hold: {
          const k = i * 3
          keepStation(wing, eid, def, wing.hold[k], wing.hold[k + 1], wing.hold[k + 2], 0, 0, 0, -1, ai.tuning.steering.turnGain, ai.tuning.steering.rollGain)
          return true
        }
        case WING_CMD.dock: {
          const k = i * 3
          const d = keepStation(wing, eid, def, wing.home[k], wing.home[k + 1], wing.home[k + 2], 0, 0, 0, -1, ai.tuning.steering.turnGain, ai.tuning.steering.rollGain)
          // Scale SC-5e2: `dockArriveM` off the home station's hull (R 0 for a point), so a km-scale station is reached.
          if (d - wing.homeR[i] <= wing.cfg.tuning.dockArriveM) wing.docked[i] = 1
          return true
        }
        default:
          return flySlot(eid, i, def)
      }
    },
    effectiveness: () => wing.effectiveness,
  }
}
